import { routes } from '@/pages'
import { URLS } from '@/pages/urls'
import { Trans } from '@lingui/react/macro'
import { type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { BuzzLinkForm } from './BuzzLinkForm'

/**
 * The design's primary button (orange fill, white Body 1 semibold, 40px tall), shared
 * by every Hive Calls CTA on `/calls`. It styles `Link`s and the non-interactive
 * `BuzzLinkForm` trigger alike, which is why it is a class string and not a `<Button>`.
 */
export const HIVE_CALLS_CTA_CLASS_NAME =
  'bg-orange flex h-10 w-fit cursor-pointer items-center justify-center whitespace-nowrap rounded-lg px-4 text-[16px]/[24px] font-semibold text-white select-none'

const BrandEmphasis = ({ children }: { children: ReactNode }) => (
  <span className="text-gray-70 font-semibold">{children}</span>
)

const Step = ({
  number,
  title,
  description,
  action
}: {
  number: number
  title: ReactNode
  description: ReactNode
  action?: ReactNode
}) => (
  <li className="flex w-full items-start gap-6">
    {/* The `<ol>` already numbers the steps for assistive tech; the ring is decoration. */}
    <span
      aria-hidden="true"
      className="border-orange text-white-to-black flex size-10 shrink-0 items-center justify-center rounded-full border text-[16px]/[24px] font-semibold"
    >
      {number}
    </span>
    {/* `items-start` keeps a CTA (and the `BuzzLinkForm` trigger button around it) at its own width. */}
    <div className="flex min-w-0 flex-1 flex-col items-start gap-5">
      <div className="flex w-full flex-col gap-2">
        <h3 className="text-subtitle text-gray-70 truncate font-semibold">{title}</h3>
        <p className="text-gray-80 text-[16px]/[24px] font-normal">{description}</p>
      </div>
      {action}
    </div>
  </li>
)

/**
 * The not-linked `/calls` content: the download hero, then the info card with its three
 * steps. One 720px column centred from `tablet:` (the 1440 artboard); below it the hero
 * stacks image-first and the column takes the full width (the 390 artboard).
 *
 * Colours follow the Figma variable names through the app's tokens, never the hex. The
 * card's gradient is the only raw colour and is dark-only; the `gray-100` border shows in
 * both themes, as the design's dark hairline does (call T″).
 *
 * The hero image swaps by theme (call G″): light uses a transparent export drawn straight
 * on the page, dark keeps the black-background export framed as a rounded panel. From
 * `tablet:` the hero row is exactly the image's 132px, so the image meets the card.
 *
 * An anonymous visitor gets a sign-in step before "Connect your accounts", and the link CTA
 * leads to the sign-in form instead of the dialog: linking needs a DataHive session.
 */
export const BuzzOnboarding = ({ isSignedIn }: { isSignedIn: boolean }) => {
  const { pathname, search } = useLocation()
  const loginUrl = `${routes.login.path}?redirect=${encodeURIComponent(`${pathname}${search}`)}`

  return (
    <div className="tablet:mx-auto tablet:gap-0 flex w-full max-w-[720px] flex-col gap-6">
      <div className="tablet:flex-row tablet:items-center tablet:gap-4 tablet:rounded-2xl tablet:pr-6 tablet:pl-10 flex flex-col-reverse">
        <p className="text-subtitle max-tablet:pt-6 tablet:flex-1 flex flex-col font-semibold">
          <span className="text-gray-70">
            <Trans>Download our new application.</Trans>
          </span>
          <span className="text-orange">
            <Trans>Hive Calls!</Trans>
          </span>
        </p>
        <div className="max-tablet:border-y flex shrink-0 justify-center border-gray-100">
          <img
            src="/images/hive-calls-hero-light@3x.png"
            className="h-[132px] w-[256px] object-cover dark:hidden"
            alt=""
          />
          <img
            src="/images/hive-calls-hero@3x.png"
            className="hidden h-[132px] w-[256px] rounded-xl object-cover dark:block"
            alt=""
          />
        </div>
      </div>

      <section className="bg-black-to-white tablet:px-10 flex flex-col items-center gap-7 overflow-hidden rounded-2xl border border-gray-100 px-4 pt-7 pb-8 dark:bg-[linear-gradient(125.6deg,#151515_0%,#000_52.33%)]">
        <div className="flex w-full flex-col gap-2">
          <h2 className="text-h5 text-gray-70 tablet:truncate">
            <Trans>Earn DataHive AI points with Hive Calls!</Trans>
          </h2>
          <div className="text-gray-80 flex flex-col gap-4 text-[16px]/[24px] font-normal">
            <p>
              <Trans>
                <BrandEmphasis>Hive Calls</BrandEmphasis> is a mobile app created for real, everyday conversations. Call
                your friends and family, talk about any topic you like, and speak in whatever language feels natural to
                you. There are no scripts or assigned topics. Just genuine conversations between real people.
              </Trans>
            </p>
            <p>
              <Trans>
                With everyone’s consent, calls made through <BrandEmphasis>Hive Calls</BrandEmphasis> are recorded.
                Eligible calls earn you DataHive points, which are automatically credited to your account once you
                connect <BrandEmphasis>Hive Calls</BrandEmphasis> to DataHive.
              </Trans>
            </p>
          </div>
        </div>

        <div aria-hidden="true" className="h-px w-full bg-gray-100" />

        <h2 className="text-h5 text-gray-70 w-full">
          <Trans>How to get started</Trans>
        </h2>

        <ol className="flex w-full flex-col gap-7">
          <Step
            number={1}
            title={<Trans>Download Hive Calls</Trans>}
            description={<Trans>Install the Hive Calls mobile app and create your Hive Calls account.</Trans>}
            action={
              <Link
                to={URLS.HIVE_CALLS_ANDROID_APP}
                target="_blank"
                rel="noreferrer"
                className={HIVE_CALLS_CTA_CLASS_NAME}
              >
                <Trans>Download Hive Calls</Trans>
              </Link>
            }
          />
          <Step
            number={2}
            title={<Trans>Get your linking code</Trans>}
            description={
              <Trans>
                Open Hive Calls, go to Profile, select “Link to DataHive AI,” tap “Generate code,” then “Copy code.”
              </Trans>
            }
          />
          {!isSignedIn && (
            <Step
              number={3}
              title={<Trans>Sign in to DataHive AI</Trans>}
              description={<Trans>Sign in or create your DataHive AI account to connect Hive Calls to it.</Trans>}
              action={
                <Link to={loginUrl} className={HIVE_CALLS_CTA_CLASS_NAME}>
                  <Trans>Sign In</Trans>
                </Link>
              }
            />
          )}
          <Step
            number={isSignedIn ? 3 : 4}
            title={<Trans>Connect your accounts</Trans>}
            description={
              <Trans>
                Paste your Hive Calls linking code. Once connected, points earned through Hive Calls will be added to
                your DataHive account.
              </Trans>
            }
            action={
              isSignedIn ? (
                <BuzzLinkForm
                  dialogTrigger={
                    <div className={HIVE_CALLS_CTA_CLASS_NAME}>
                      <Trans>Link account</Trans>
                    </div>
                  }
                />
              ) : (
                <Link to={loginUrl} className={HIVE_CALLS_CTA_CLASS_NAME}>
                  <Trans>Link account</Trans>
                </Link>
              )
            }
          />
        </ol>
      </section>
    </div>
  )
}
